import { decodeJwt } from 'jose'

/**
 * Auth utilities — token storage + JWT decode.
 *
 * Tokens persist in `localStorage` (same as V1, sessions last 30
 * days via the refresh token). We do not use cookies because the
 * PWA talks to the API across origins in dev (Vite proxy in dev,
 * different host in prod) and `Authorization: Bearer` in
 * `localStorage` is the simplest path that works in both.
 *
 * Security trade-off: an XSS could steal the tokens. We mitigate
 * by:
 *   - Strict CSP in `index.html` once the production deploy lands.
 *   - Tight access-token TTL (15 min — set by the API).
 *   - Refresh tokens that the API can revoke server-side once we
 *     wire a `revoked_jtis` table (planned).
 *
 * Token shape comes from `apps/api/src/lib/jwt.ts` — `sub`
 * (userId), `tenantId`, `role`, plus standard claims.
 */

const ACCESS_KEY = 'avity:access_token'
const REFRESH_KEY = 'avity:refresh_token'

export interface DecodedUser {
  id: string
  tenantId: string
  role: 'super_admin' | 'admin' | 'driver'
}

/* -------------------------------------------------------------------------- */
/*  localStorage I/O                                                           */
/* -------------------------------------------------------------------------- */

export function readTokens(): { accessToken: string | null; refreshToken: string | null } {
  if (typeof localStorage === 'undefined') {
    // SSR / very early React render before the DOM is ready —
    // returns "logged out" so the auth guard redirects.
    return { accessToken: null, refreshToken: null }
  }
  return {
    accessToken: localStorage.getItem(ACCESS_KEY),
    refreshToken: localStorage.getItem(REFRESH_KEY),
  }
}

export function writeTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_KEY, accessToken)
  localStorage.setItem(REFRESH_KEY, refreshToken)
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY)
  localStorage.removeItem(REFRESH_KEY)
}

/* -------------------------------------------------------------------------- */
/*  JWT decode                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Decode a JWT and pull the `(id, tenantId, role)` triple out of
 * its claims. **Does NOT verify the signature** — the API does
 * that, the client just reads.
 *
 * Returns `null` for any malformed token, expired token (per `exp`
 * claim), or token that lacks the expected claims. Callers treat
 * `null` as "act as if logged out" without throwing.
 */
export function decodeUser(token: string): DecodedUser | null {
  try {
    const claims = decodeJwt(token) as {
      sub?: string
      tenantId?: string
      role?: string
      exp?: number
    }
    // exp is in seconds; Date.now() in ms. Treat a token in the
    // past as logged out; the API would reject it anyway with
    // TOKEN_EXPIRED.
    if (typeof claims.exp === 'number' && claims.exp * 1000 < Date.now()) {
      return null
    }
    if (
      typeof claims.sub !== 'string' ||
      typeof claims.tenantId !== 'string' ||
      typeof claims.role !== 'string' ||
      !['super_admin', 'admin', 'driver'].includes(claims.role)
    ) {
      return null
    }
    return {
      id: claims.sub,
      tenantId: claims.tenantId,
      role: claims.role as DecodedUser['role'],
    }
  } catch {
    return null
  }
}

/**
 * Quick boolean check used by route guards before they bother
 * decoding the full payload. Returns `false` for missing or
 * malformed tokens.
 */
export function isTokenLikelyValid(token: string | null): boolean {
  if (token === null) return false
  return decodeUser(token) !== null
}
